import { Playlist } from '../models/Playlists';
import firebase from 'firebase/compat/app';
import { getViewer, viewerCollectionTier } from './Viewer';
const firestore=firebase.firestore

/**
 * Provable reads for an isPrivate-flagged collection (playlists, albums).
 * The rules only let a list query through when every possible result is
 * readable, so non-staff viewers run `isPrivate == false` plus (signed in)
 * `ownerId == uid` and merge (guests additionally `privacy == "public"`,
 * the creator's profile tier); staff run one unfiltered query. Docs missing
 * `isPrivate` are private to their owner by rule (S06), so the public slice
 * deliberately excludes them.
 */
export const fetchVisibleCollection = async (collectionName: string) => {
  const db = firestore();
  const viewer = getViewer();
  const base = db.collection(collectionName);
  // Shared (non-private) collections are readable by any signed-in user;
  // guests only see those whose creator's profile is public (privacy tier).
  const queries: firebase.firestore.Query[] = viewer.staff
    ? [base]
    : viewer.uid
      ? [base.where('isPrivate', '==', false), base.where('ownerId', '==', viewer.uid)]
      : [base.where('isPrivate', '==', false).where('privacy', '==', 'public')];
  const snapshots = await Promise.all(queries.map((q) => q.get()));
  const byId: Record<string, firebase.firestore.QueryDocumentSnapshot> = {};
  snapshots.forEach((snap) => snap.docs.forEach((d) => { byId[d.id] = d; }));
  return Object.values(byId);
};


export class PlaylistService {
  static StronglyTypePlaylistData = (playlistDoc: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData> | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>) => {
    return { ...playlistDoc.data(), id: playlistDoc.id } as Playlist;
  }

  static transmit = (freshPlaylist: Playlist) => {
    const db = firestore();
    // Always explicit: the rules require a boolean isPrivate on create.
    return db.collection('playlists').doc(freshPlaylist.id).set({ ...freshPlaylist, isPrivate: !!freshPlaylist.isPrivate, privacy: viewerCollectionTier() });
  }

  static fetchAll = () => {
    return new Promise<Playlist[]>((resolve, reject) => {
      fetchVisibleCollection('playlists').then(docs => {
        resolve(docs.map(playlistDoc => PlaylistService.StronglyTypePlaylistData(playlistDoc)));
      }).catch(reject);
    });
  }

  static fetch = (id: string) => {
    return new Promise<Playlist>((resolve, reject) => {
      const db = firestore();
      db.collection('playlists').doc(id).get().then(playlistDoc => {
        const playlist = PlaylistService.StronglyTypePlaylistData(playlistDoc);
        resolve(playlist);
      }).catch(reject);
    });
  }

  static delete = (id: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const db = firestore();
      if (!id || id === '') {
        return;
      }
      db.collection('playlists').doc(id).delete().then(() => {
        resolve();
      });
    })
  }

  static update = (playlist: Playlist): Promise<Playlist> => {
    return new Promise<Playlist>((resolve, reject) => {
      const db = firestore();
      const serverPlayList = db.collection('playlists').doc(playlist.id);
      // The owner's edits re-stamp the audience tier from their current profile.
      const own = playlist.ownerId === getViewer().uid;
      serverPlayList.set(own ? { ...playlist, privacy: viewerCollectionTier() } : playlist, { merge: true }).then(() => {
        serverPlayList.get().then((playlistDoc) => {
          const playlist = PlaylistService.StronglyTypePlaylistData(playlistDoc);
          resolve(playlist);
        }).catch(reject);
      });
    });
  }
}


